/**
 * variant-popover.js
 * Custom element for variant selection on FREE_GIFT cart items.
 * Mirrors QuantityInput / quantity-popover patterns.
 *
 * Place in: /assets/variant-popover.js
 * Include via theme.liquid or cart-drawer.liquid:
 *   <script src="{{ 'variant-popover.js' | asset_url }}" defer></script>
 */

class VariantPopover extends HTMLElement {
  constructor() {
    super();

    this.trigger = this.querySelector('.variant-popover__trigger');
    this.panel = this.querySelector('.variant-popover__info');
    this.options = this.querySelectorAll('.variant-popover__option');
    this.closeBtn = this.querySelector('.variant-popover__close');
    this.freeProductAndGifts = new FreeProductAndGifts();

    this._boundClose = this._onOutsideClick.bind(this);

    if (this.trigger) {
      this.trigger.addEventListener('click', this._onTriggerClick.bind(this));
    }

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.close.bind(this));
    }

    this.options.forEach(option => {
      option.addEventListener('click', this._onOptionClick.bind(this));
    });
  }

  /* ── Open / close ─────────────────────────────────────────────────────── */

  open() {
    this.panel.removeAttribute('hidden');
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this._boundClose);
  }

  close() {
    this.panel.setAttribute('hidden', '');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._boundClose);
  }

  _onTriggerClick(event) {
    event.stopPropagation();
    const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
    isOpen ? this.close() : this.open();
  }

  _onOutsideClick(event) {
    if (!this.contains(event.target)) this.close();
  }

  /* ── Option selection ─────────────────────────────────────────────────── */

  _onOptionClick(event) {
    const option = event.currentTarget;
    if (option.disabled) return;

    const variantId = option.dataset.variantId;
    const lineKey = this.dataset.lineKey;
    const label = option.dataset.variantTitle;
    const line = option.dataset.index;

    // Update trigger label immediately for perceived performance
    const valueEl = this.trigger.querySelector('.variant-popover__trigger-value');
    if (valueEl) valueEl.textContent = label;

    // Update aria-selected on all options
    this.options.forEach(o => o.setAttribute('aria-selected', 'false'));
    option.setAttribute('aria-selected', 'true');

    this.close();
    this._swapVariant(line, lineKey, variantId);
  }

  enableLoading(line) {
    const selectedCartItem = [...document.querySelectorAll(`#CartItem-${line} .loading__spinner`),
    ...document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)]

    selectedCartItem.forEach(el => el.classList.remove('hidden'));

    document.activeElement.blur();
  }

  disableLoading(line) {
    const selectedCartItem = [...document.querySelectorAll(`#CartItem-${line} .loading__spinner`),
    ...document.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`)]

    selectedCartItem.forEach(el => el.classList.add('hidden'));
  }

  /* ── Cart variant swap ────────────────────────────────────────────────── */

  async _swapVariant(line, lineKey, variantId) {
    this.enableLoading(line)

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'updates': {
            [lineKey]: 0,
            [parseInt(variantId)]: 1,
          }
        }),
      });

      if (!response.ok) throw new Error(`cart/update.js error: ${response.status}`);
      await response.json();
      this.freeProductAndGifts.checkFreeProductAndGifts();

    } catch (error) {
      console.error('Variant swap failed:', error);
    } finally {
      this.disableLoading(line)
    }
  }
}

customElements.define('variant-popover', VariantPopover);
